import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useResolve } from '@visiba/instantiation';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';

export type RespondentType = 'me' | 'representative';

interface FormData {
	respondentType: RespondentType;
	asAgentFor: Nullable<string>;
}

export function useScreenWhoIsRespondant() {
	const viewBooking = useBooking();
	const analyticService = useResolve(AnalyticService);
	const bookingBasket = useBookingBasket();
	const { currentlyRepresentedPatient, representablePatients, isLoading: isLoadingRepresentation } = useRepresentablePatients();
	const initialBasketRef = useRef(bookingBasket.getBasket());

	const allowRepresentative = viewBooking.flowData.status === 'accessible' ? viewBooking.flowData.value.allowRepresentative : true;

	const [data, setData] = useState({
		respondentType: initialBasketRef.current.respondantType,
		representativeNin: initialBasketRef.current.asAgentFor,
		representativeNinPlaceholder: '',
	});

	const submit = useCallback(
		async (formData: FormData) => {
			const flowData = viewBooking.getFlowData();
			await bookingBasket.setBasket({
				respondantType: formData.respondentType === 'representative' ? 'representative' : 'me',
				asAgentFor:
					formData.respondentType === 'representative'
						? NiNFormatter.format(flowData.currentCountry, NiN.normalize(flowData.currentCountry, (formData.asAgentFor ?? '').trim()))
						: null,
			});
			viewBooking.goToScreen(BookingScreen.ReasonForVisit);
		},
		[bookingBasket.setBasket, viewBooking.getFlowData, viewBooking.goToScreen],
	);

	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'Booking: Who is respondant' });
	}, [analyticService]);

	useEffect(() => {
		bookingBasket.unlockTimeSlot();
		(async () => {
			if (viewBooking.flowData.status !== 'accessible') {
				return;
			}

			const flowSettings = viewBooking.getFlowSettings();

			// Deep-linked representative (e.g. RedRobin) is only honoured when "Patient med ombud" is active.
			if (allowRepresentative && flowSettings.asAgentFor) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetRespondantTypeRepresentative);
				await bookingBasket.setBasket({ respondantType: 'representative', asAgentFor: flowSettings.asAgentFor });
				viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
				return;
			}

			if (isLoadingRepresentation) {
				return;
			}

			// Skip screen if the user is currently representing someone (e.g. a child)
			if (currentlyRepresentedPatient) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetRespondantTypeRepresentative);
				await bookingBasket.setBasket({
					respondantType: 'representative',
					asAgentFor: null,
				});
				viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
				return;
			}

			// "Patient med ombud" (arbitrary representative) is inactive. If the patient also has no
			// representable persons (e.g. children) to select, there is nobody they can represent, so
			// skip the step and continue as themselves.
			if (!allowRepresentative && representablePatients.length === 0) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.DoesNotAllowRepresentative);
				await bookingBasket.setBasket({ respondantType: 'me', asAgentFor: null });
				viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
				return;
			}

			if (flowSettings.redRobinSessionId && !flowSettings.asAgentFor) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetRespondantTypeMe);
				await bookingBasket.setBasket({ respondantType: 'me', asAgentFor: null });
				viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
				return;
			}

			const basket = bookingBasket.getBasket();
			setData({
				respondentType: basket.asAgentFor ? 'representative' : 'me',
				representativeNin: basket.asAgentFor,
				representativeNinPlaceholder: NiNFormatter.formatWithPlaceholder(viewBooking.getFlowData().currentCountry, '')[1] ?? '',
			});
		})();
	}, [
		analyticService,
		allowRepresentative,
		isLoadingRepresentation,
		currentlyRepresentedPatient,
		representablePatients,
		bookingBasket.unlockTimeSlot,
		bookingBasket.setBasket,
		bookingBasket.getBasket,
		viewBooking.flowData,
		viewBooking.getFlowSettings,
		viewBooking.getFlowData,
		viewBooking.goToScreen,
	]);

	return { data, submit, allowRepresentative };
}
